/**
 * Headless selectors + formatters for the Insights → Revenue Bridge tab.
 *
 * Platform-neutral (no DOM, no recharts) per the shared-core contract. The
 * web-shell chart component consumes these to derive the waterfall steps and
 * the KPI strip from the server-computed bridge. The server has already
 * classified customers and summed the per-bucket deltas — these are pure
 * projections into the (floating-bar) waterfall geometry, no aggregation.
 */

import type {
  InsightsRevenueBridgeBucket,
  InsightsRevenueBridgeFunnel,
  InsightsRevenueBridgeResponse,
} from "../data/api-types";
import { formatGbpCompact } from "./insights-monthly-revenue";

export { formatGbpCompact };

/** Which measure the waterfall is drawn on. */
export type BridgeMeasure = "revenue" | "contribution";

/**
 * One bar of the waterfall. ``anchor`` steps (prior / current totals) sit on
 * the floor; ``delta`` steps float between ``base`` and ``top`` so the bar's
 * height IS the magnitude of the move and its sign gives the colour.
 */
export interface WaterfallStep {
  /** Stable key + x-axis label. */
  key: "prior" | "new" | "grown" | "shrunk" | "lost" | "current";
  label: string;
  kind: "anchor" | "delta";
  /** Signed value: the total for anchors, the delta for steps. */
  value: number;
  /** Floating-bar bottom (running total before this step, clamped for deltas). */
  base: number;
  /** Floating-bar top. */
  top: number;
  /** Customer count behind a delta step (undefined for anchors). */
  customerCount?: number;
}

const _BUCKET_LABEL: Record<InsightsRevenueBridgeBucket["bucket"], string> = {
  new: "New",
  grown: "Grown",
  shrunk: "Shrunk",
  lost: "Lost",
};

/** Pull the four buckets in canonical waterfall order (new → grown → shrunk → lost). */
function orderedBuckets(
  bridge: InsightsRevenueBridgeResponse,
): InsightsRevenueBridgeBucket[] {
  const order: InsightsRevenueBridgeBucket["bucket"][] = [
    "new",
    "grown",
    "shrunk",
    "lost",
  ];
  return order.map(
    (name) =>
      bridge.buckets.find((b) => b.bucket === name) ?? {
        bucket: name,
        customer_count: 0,
        revenue_delta: 0,
        contribution_delta: 0,
      },
  );
}

function deltaFor(
  bucket: InsightsRevenueBridgeBucket,
  measure: BridgeMeasure,
): number {
  return measure === "revenue"
    ? bucket.revenue_delta
    : bucket.contribution_delta;
}

function totals(
  bridge: InsightsRevenueBridgeResponse,
  measure: BridgeMeasure,
): { prior: number; current: number } {
  return measure === "revenue"
    ? {
        prior: bridge.prior_revenue_total,
        current: bridge.current_revenue_total,
      }
    : {
        prior: bridge.prior_contribution_total,
        current: bridge.current_contribution_total,
      };
}

/**
 * Build the six waterfall steps (prior anchor, four cohort deltas, current
 * anchor) for the chosen measure. The running total after the four deltas
 * equals the current anchor by construction (the bridge closes).
 */
export function toWaterfallSteps(
  bridge: InsightsRevenueBridgeResponse,
  measure: BridgeMeasure,
): WaterfallStep[] {
  const { prior, current } = totals(bridge, measure);
  const steps: WaterfallStep[] = [
    {
      key: "prior",
      label: "Prior yr",
      kind: "anchor",
      value: prior,
      base: 0,
      top: prior,
    },
  ];

  let running = prior;
  for (const bucket of orderedBuckets(bridge)) {
    const value = deltaFor(bucket, measure);
    const from = running;
    const to = running + value;
    running = to;
    steps.push({
      key: bucket.bucket,
      label: _BUCKET_LABEL[bucket.bucket],
      kind: "delta",
      value,
      base: Math.min(from, to),
      top: Math.max(from, to),
      customerCount: bucket.customer_count,
    });
  }

  steps.push({
    key: "current",
    label: "Current",
    kind: "anchor",
    value: current,
    base: 0,
    top: current,
  });
  return steps;
}

/** Net change on a measure (current − prior). */
export function netDelta(
  bridge: InsightsRevenueBridgeResponse,
  measure: BridgeMeasure,
): number {
  const { prior, current } = totals(bridge, measure);
  return current - prior;
}

/** Contribution margin (0..1) for a period, guarding divide-by-zero. */
function marginOf(contribution: number, revenue: number): number | null {
  if (!revenue) return null;
  return contribution / revenue;
}

/** Prior-period contribution margin (0..1), or null when prior revenue is zero. */
export function priorMargin(
  bridge: InsightsRevenueBridgeResponse,
): number | null {
  return marginOf(bridge.prior_contribution_total, bridge.prior_revenue_total);
}

/** Current-period contribution margin (0..1), or null when current revenue is zero. */
export function currentMargin(
  bridge: InsightsRevenueBridgeResponse,
): number | null {
  return marginOf(
    bridge.current_contribution_total,
    bridge.current_revenue_total,
  );
}

/**
 * Margin-mix shift in percentage points (current margin − prior margin), or
 * null when either period has no revenue. The headline of the whole tab: the
 * business swapped low-margin revenue for high-margin revenue.
 */
export function marginShiftPct(
  bridge: InsightsRevenueBridgeResponse,
): number | null {
  const prior = priorMargin(bridge);
  const current = currentMargin(bridge);
  if (prior == null || current == null) return null;
  return 100 * (current - prior);
}

/** Total customers moved across the four cohorts (KPI). */
export function movedCustomerCount(
  bridge: InsightsRevenueBridgeResponse,
): number {
  return bridge.buckets.reduce((s, b) => s + b.customer_count, 0);
}

/** Signed compact GBP for a delta — "+£1.3M" / "−£877k". */
export function formatSignedGbpCompact(n: number): string {
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${formatGbpCompact(Math.abs(n))}`;
}

/** Signed percentage-point label — "+37pp" / "−4pp". */
export function formatSignedPp(pp: number): string {
  const sign = pp > 0 ? "+" : pp < 0 ? "−" : "";
  return `${sign}${Math.round(Math.abs(pp))}pp`;
}

/** Margin (0..1) as a whole-percent label, or "—" when null. */
export function formatMarginPct(margin: number | null): string {
  return margin == null ? "—" : `${Math.round(100 * margin)}%`;
}

// ---- Margin-weighted callout ------------------------------------------------
//
// The punchline: the business swapped low-margin revenue for high-margin
// revenue. "Gained" = new + grown cohorts; "shed" = shrunk + lost. Each side
// carries the blended margin of the revenue it moved (contribution Δ ÷ revenue Δ).

function bucketByName(
  bridge: InsightsRevenueBridgeResponse,
  name: InsightsRevenueBridgeBucket["bucket"],
): InsightsRevenueBridgeBucket {
  return (
    bridge.buckets.find((b) => b.bucket === name) ?? {
      bucket: name,
      customer_count: 0,
      revenue_delta: 0,
      contribution_delta: 0,
    }
  );
}

export interface CalloutSide {
  /** Signed revenue moved by this side (gained > 0, shed < 0). */
  revenueDelta: number;
  /** Signed contribution moved by this side. */
  contributionDelta: number;
  /** Blended margin of the moved revenue (0..1), or null when no revenue moved. */
  margin: number | null;
}

/** Revenue + contribution + blended margin gained (new + grown). */
export function gainedSide(bridge: InsightsRevenueBridgeResponse): CalloutSide {
  const nu = bucketByName(bridge, "new");
  const grown = bucketByName(bridge, "grown");
  const revenueDelta = nu.revenue_delta + grown.revenue_delta;
  const contributionDelta = nu.contribution_delta + grown.contribution_delta;
  return {
    revenueDelta,
    contributionDelta,
    margin: marginOf(contributionDelta, revenueDelta),
  };
}

/** Revenue + contribution + blended margin shed (shrunk + lost). */
export function shedSide(bridge: InsightsRevenueBridgeResponse): CalloutSide {
  const shrunk = bucketByName(bridge, "shrunk");
  const lost = bucketByName(bridge, "lost");
  const revenueDelta = shrunk.revenue_delta + lost.revenue_delta;
  const contributionDelta = shrunk.contribution_delta + lost.contribution_delta;
  return {
    revenueDelta,
    // margin of shed revenue: contribution shed ÷ revenue shed (both negative,
    // so the ratio is positive — the margin of the work that left).
    contributionDelta,
    margin: marginOf(contributionDelta, revenueDelta),
  };
}

/**
 * Rate-vs-volume decomposition of the contribution change. If the business had
 * simply grown/shrunk its existing revenue at the prior margin, contribution
 * would move by the "volume" part; the remainder — contribution the margin mix
 * delivered on top — is the "rate" part. This is the callout's "+£496k rate ·
 * −£5k volume" split.
 *
 * volume = revenueΔ × priorMargin ; rate = contributionΔ − volume.
 */
export interface RateVolumeSplit {
  rate: number;
  volume: number;
}

export function rateVolumeSplit(
  bridge: InsightsRevenueBridgeResponse,
): RateVolumeSplit {
  const pm = priorMargin(bridge) ?? 0;
  const revenueChange = netDelta(bridge, "revenue");
  const contributionChange = netDelta(bridge, "contribution");
  const volume = revenueChange * pm;
  return { rate: contributionChange - volume, volume };
}

// ---- Estimate funnel --------------------------------------------------------
//
// The leading indicator: current TTM vs prior TTM. Each metric renders as a
// "prior → current" pair with a signed change (percent for £/count metrics,
// percentage-points for the win rates). Derived from the server-supplied funnel
// block — no aggregation, just presentation shaping.

export interface FunnelRow {
  key: string;
  label: string;
  /** Formatted "prior → current" value pair. */
  valueText: string;
  /** Formatted signed change ("−28%" / "+6pp"). */
  changeText: string;
  /** Sign of the change, for colour: 1 up, -1 down, 0 flat. */
  direction: 1 | -1 | 0;
}

function pctChange(prior: number, current: number): number | null {
  if (!prior) return null;
  return (100 * (current - prior)) / prior;
}

function directionOf(delta: number | null): 1 | -1 | 0 {
  if (delta == null || delta === 0) return 0;
  return delta > 0 ? 1 : -1;
}

function formatSignedPct(pct: number | null): string {
  if (pct == null) return "—";
  const sign = pct > 0 ? "+" : pct < 0 ? "−" : "";
  return `${sign}${Math.round(Math.abs(pct))}%`;
}

/** Compact count label — "5,347". */
function formatCount(n: number): string {
  return Math.round(n).toLocaleString("en-GB");
}

/**
 * Build the five funnel rows in the mockup's order: enquiries, quoted value,
 * won value, win-rate (value), win-rate (count). Enquiries/value rows carry a
 * percent change; win-rate rows carry a percentage-point change.
 */
export function funnelRows(funnel: InsightsRevenueBridgeFunnel): FunnelRow[] {
  const enquiriesPct = pctChange(
    funnel.enquiries_prior,
    funnel.enquiries_current,
  );
  const quotedPct = pctChange(
    funnel.quoted_value_prior,
    funnel.quoted_value_current,
  );
  const wonPct = pctChange(funnel.won_value_prior, funnel.won_value_current);
  const winValuePp =
    100 * (funnel.win_rate_value_current - funnel.win_rate_value_prior);
  const winCountPp =
    100 * (funnel.win_rate_count_current - funnel.win_rate_count_prior);

  return [
    {
      key: "enquiries",
      label: "Enquiries quoted",
      valueText: `${formatCount(funnel.enquiries_prior)} → ${formatCount(funnel.enquiries_current)}`,
      changeText: formatSignedPct(enquiriesPct),
      direction: directionOf(enquiriesPct),
    },
    {
      key: "quoted_value",
      label: "Quoted value",
      valueText: `${formatGbpCompact(funnel.quoted_value_prior)} → ${formatGbpCompact(funnel.quoted_value_current)}`,
      changeText: formatSignedPct(quotedPct),
      direction: directionOf(quotedPct),
    },
    {
      key: "won_value",
      label: "Won value",
      valueText: `${formatGbpCompact(funnel.won_value_prior)} → ${formatGbpCompact(funnel.won_value_current)}`,
      changeText: formatSignedPct(wonPct),
      direction: directionOf(wonPct),
    },
    {
      key: "win_rate_value",
      label: "Win rate (value)",
      valueText: `${formatMarginPct(funnel.win_rate_value_prior)} → ${formatMarginPct(funnel.win_rate_value_current)}`,
      changeText: formatSignedPp(winValuePp),
      direction: directionOf(winValuePp),
    },
    {
      key: "win_rate_count",
      label: "Win rate (count)",
      valueText: `${formatMarginPct(funnel.win_rate_count_prior)} → ${formatMarginPct(funnel.win_rate_count_current)}`,
      changeText: formatSignedPp(winCountPp),
      direction: directionOf(winCountPp),
    },
  ];
}

// ---- Thesis -----------------------------------------------------------------

/**
 * The computed thesis sentence — the "swapped low-margin for high-margin"
 * narrative, filled from the data. Reads the gained/shed sides and the net
 * moves so the prose is never stale relative to the numbers.
 */
export function thesisText(bridge: InsightsRevenueBridgeResponse): string {
  const gained = gainedSide(bridge);
  const shed = shedSide(bridge);
  const revNet = netDelta(bridge, "revenue");
  const conNet = netDelta(bridge, "contribution");
  const priorCon = bridge.prior_contribution_total;
  const conPct = priorCon ? Math.round((100 * conNet) / priorCon) : null;

  const revWord =
    Math.abs(revNet) < 0.02 * (bridge.prior_revenue_total || 1)
      ? "was essentially flat"
      : revNet >= 0
        ? `rose ${formatSignedGbpCompact(revNet)}`
        : `fell ${formatSignedGbpCompact(revNet)}`;

  const conWord =
    conPct == null
      ? `moved ${formatSignedGbpCompact(conNet)}`
      : `${conNet >= 0 ? "rose" : "fell"} ${Math.abs(conPct)}%`;

  return (
    `The business swapped low-margin revenue for high-margin revenue: ` +
    `it added ${formatGbpCompact(Math.abs(gained.revenueDelta))} at ` +
    `${formatMarginPct(gained.margin)} margin (new + growing accounts) and shed ` +
    `${formatGbpCompact(Math.abs(shed.revenueDelta))} at ${formatMarginPct(shed.margin)} ` +
    `margin (shrinking + lost accounts). Net revenue ${revWord}, but ` +
    `contribution ${conWord}.`
  );
}
